import { useEffect, useMemo, useRef } from 'react'
import type { PropsWithChildren } from 'react'
import type { AxiosInstance } from 'axios'
import * as Sentry from '@sentry/react'
import { useTranslation } from 'react-i18next'
import { useAuthentication } from '@mysaiva/core/authentication'
import { OrgProvider, useOrg } from '@mysaiva/core/organizations'
import { ApplicationContextProvider } from '@mysaiva/core/application'
import { useLaunchDarkly, getAnonymousUserKey } from '@mysaiva/core/launchdarkly'
import { NoAccessPage } from '@mysaiva/core/components'
import {
  getBackToSaivaPath,
  getDeveloperPlatformOrgIdKey,
  resolveDeveloperPlatformOrgSource
} from '@mysaiva/core/developer-platform'
import { WORKSPACE_LD_PRODUCT } from '../constants'
import { WorkspaceOrgSourceContext } from './WorkspaceOrgSourceContext'

/**
 * Moves the LaunchDarkly identity from the anonymous pre-auth key to the
 * signed-in user (with org) so per-organisation targeting of the
 * developer-platform flag works, and re-anonymises on logout. Mirrors the
 * product apps' `LaunchDarklyIdentitySync`.
 */
function LaunchDarklyIdentitySync() {
  const { user } = useAuthentication()
  const { org } = useOrg()
  const ld = useLaunchDarkly()
  const hasIdentifiedRef = useRef(false)

  const userId = user?.id
  const isSuperuser = user?.is_superuser ?? false
  const orgId = org?.id
  const userRole = org?.user_role ?? undefined

  useEffect(() => {
    if (ld.isDisabled) return

    if (userId != null) {
      hasIdentifiedRef.current = true
      ld.identify({
        user_id: String(userId),
        org_id: orgId,
        product: WORKSPACE_LD_PRODUCT,
        environment: import.meta.env.MODE,
        role: userRole,
        user_type: isSuperuser ? 'internal' : 'external'
      }).catch(err => Sentry.captureException(err))
      return
    }

    if (hasIdentifiedRef.current) {
      hasIdentifiedRef.current = false
      ld.identify({
        user_id: getAnonymousUserKey(),
        anonymous: true,
        product: WORKSPACE_LD_PRODUCT,
        environment: import.meta.env.MODE
      }).catch(err => Sentry.captureException(err))
    }
  }, [userId, orgId, userRole, isSuperuser, ld])

  return null
}

function OrgLoadError() {
  const { t } = useTranslation()
  return (
    <NoAccessPage
      title={t('developerPlatform.access.noOrgTitle')}
      message={t('developerPlatform.access.noOrgMessage')}
      buttonLabel={t('developerPlatform.backToSaiva')}
      onButtonClick={() => window.location.assign(getBackToSaivaPath())}
    />
  )
}

/**
 * Bridges the selected organisation into core's `ApplicationContext`, which
 * every access-control hook (`useHasPermissions`, `AccessControl`,
 * `useDeveloperPlatformAccess`) reads from.
 */
function WorkspaceApplicationContext({
  apiClient,
  children
}: PropsWithChildren<{ apiClient: AxiosInstance }>) {
  const orgContext = useOrg()
  return (
    <ApplicationContextProvider apiClient={apiClient} orgContext={orgContext}>
      <LaunchDarklyIdentitySync />
      {children}
    </ApplicationContextProvider>
  )
}

interface DeveloperPlatformProvidersProps {
  apiClient: AxiosInstance
}

/**
 * Org + application context for the workspace. Rendered only after the
 * authentication and feature-flag checks in `DeveloperPlatformGate` pass.
 *
 * The org list comes from a product in the user's `product_list` (UPT or
 * Compass, see `resolveDeveloperPlatformOrgSource`), sharing that product
 * app's stored org selection.
 */
export function DeveloperPlatformProviders({
  apiClient,
  children
}: PropsWithChildren<DeveloperPlatformProvidersProps>) {
  const { user } = useAuthentication()
  const productList = user?.product_list
  const orgSource = useMemo(() => resolveDeveloperPlatformOrgSource(productList), [productList])

  if (!orgSource) {
    return <OrgLoadError />
  }

  return (
    <WorkspaceOrgSourceContext.Provider value={orgSource}>
      <OrgProvider
        productName={orgSource}
        apiClient={apiClient}
        orgIdKey={getDeveloperPlatformOrgIdKey(orgSource)}
        ErrorComponent={OrgLoadError}
      >
        <WorkspaceApplicationContext apiClient={apiClient}>{children}</WorkspaceApplicationContext>
      </OrgProvider>
    </WorkspaceOrgSourceContext.Provider>
  )
}
